import { useState } from 'react';
import { ApiClient } from '@chefaiq/shared/lib/api-client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Upload, X, File, Image as ImageIcon } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { showSuccess, showError } from '@/lib/toastHelpers';

interface FileUploadProps {
  onFileUploaded: (file: { url: string; fileName: string; fileType: string }) => void;
  endpoint: string;
  accept?: string;
  maxSizeMB?: number;
}

export function FileUpload({ onFileUploaded, endpoint, accept = '*/*', maxSizeMB = 10 }: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  

  const handleUpload = async (file: File) => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      showError(`Maximum file size is ${maxSizeMB}MB`, "File too large");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const apiUrl = import.meta.env.VITE_API_URL || '';
      const fullEndpoint = endpoint.startsWith('http') ? endpoint : `${apiUrl}${endpoint}`;
      
      const token = ApiClient.getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(fullEndpoint, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token.replace(/^["']|["']$/g, '')}` // Remove quotes if present
        }
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();

      if (result.success || result.url) {
        onFileUploaded({
          url: result.url,
          fileName: result.fileName || file.name,
          fileType: result.fileType || file.type
        });
        showSuccess(`${file.name} uploaded successfully`, "File uploaded");
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to upload file", "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <input
        type="file"
        id="file-upload"
        className="hidden"
        accept={accept}
        onChange={(e) => {
          if (e.target.files?.[0]) {
            handleUpload(e.target.files[0]);
            e.target.value = ''; // Reset input to allow same file selection
          }
        }}
        disabled={isUploading}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isUploading}
        onClick={() => document.getElementById('file-upload')?.click()}
      >
        <Upload className="h-4 w-4 mr-2" />
        {isUploading ? 'Uploading...' : 'Attach File'}
      </Button>
    </div>
  );
}

interface AttachmentPreviewProps {
  url: string;
  fileName: string;
  fileType: string;
  onRemove?: () => void;
}

export function AttachmentPreview({ url, fileName, fileType, onRemove }: AttachmentPreviewProps) {
  if (!url || !fileType) return null;
  
  const isImage = fileType.startsWith('image/');

  return (
    <div className="relative inline-block mr-2 mb-2">
      {isImage ? (
        <div className="relative">
          <img src={url} alt={fileName} className="h-20 w-20 object-cover rounded border" />
          {onRemove && (
            <Button
              size="icon"
              variant="destructive"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
              onClick={onRemove}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 p-2 border rounded bg-muted">
          <File className="h-4 w-4" />
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline max-w-[150px] truncate">
            {fileName}
          </a>
          {onRemove && (
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onRemove}>
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
